import {useState} from 'react'
import {Modal, Button, Table, Form, Badge, Alert} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {GET_ALL_TAGS} from '../../../gql/queries/tagsQuery'
import {ADD_EDIT_TAG, DELETE_TAG_INSURANCE} from '../../../gql/mutations/tagMutations'
import {IResTags, IResAddTag, IAllTagNode} from '../../../types/tags'
import {useGetUser} from '../../../store/userStore'
import {parseId} from '../../../helpers'

const getContrastColor = (hex: string): string => {
  const clean = (hex || '').replace('#', '')
  const r = parseInt(clean.substring(0, 2), 16)
  const g = parseInt(clean.substring(2, 4), 16)
  const b = parseInt(clean.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#000000' : '#ffffff'
}

interface LeadTagManagerProps {
  show: boolean
  onHide: () => void
  onTagsUpdated?: () => void
}

export const LeadTagManager: React.FC<LeadTagManagerProps> = ({show, onHide, onTagsUpdated}) => {
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const currentOrgId = useGetUser((s) => s.user?.user?.organization?.id)

  const [editingTag, setEditingTag] = useState<IAllTagNode | null>(null)
  const [formData, setFormData] = useState({name: '', color: '#3699ff'})

  const {data, loading, refetch} = useQuery<IResTags>(GET_ALL_TAGS, {
    skip: !show,
    fetchPolicy: 'no-cache',
  })

  const invalidateTagsCache = (cache: import('@apollo/client').ApolloCache<any>) => {
    cache.evict({fieldName: 'allTagInsurances'})
    cache.gc()
  }

  const [gqlAddEditTag, {loading: saving}] = useMutation<IResAddTag>(ADD_EDIT_TAG, {
    update: (cache) => invalidateTagsCache(cache),
  })
  const [gqlDeleteTag] = useMutation(DELETE_TAG_INSURANCE, {
    update: (cache) => invalidateTagsCache(cache),
  })

  const tags = data?.allTagInsurances?.edges.map(({node}) => node) || []

  const canManage = (tag: IAllTagNode) => {
    if (isSuperAdmin) return true
    if (!tag.organization) return false
    return String(parseId(tag.organization.id)) === String(parseId(currentOrgId || ''))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const result = await gqlAddEditTag({
        variables: {
          input: {
            id: editingTag ? parseId(editingTag.id) : undefined,
            name: formData.name,
            color: formData.color,
          },
        },
      })

      if (result.data?.tagInsuranceCreateupdateMutation?.tagInsurance?.id) {
        toast.success(`Tag ${editingTag ? 'updated' : 'created'} successfully`)
        setFormData({name: '', color: '#3699ff'})
        setEditingTag(null)
        refetch()
        onTagsUpdated?.()
      } else if (result.data?.tagInsuranceCreateupdateMutation?.errors?.length) {
        toast.error(result.data.tagInsuranceCreateupdateMutation.errors[0]?.messages?.[0] || 'Failed to save tag')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save tag')
    }
  }

  const handleEdit = (tag: IAllTagNode) => {
    setEditingTag(tag)
    setFormData({name: tag.name, color: tag.color})
  }

  const handleDelete = async (tag: IAllTagNode) => {
    if (!window.confirm('Are you sure you want to delete this tag?')) return

    try {
      await gqlDeleteTag({variables: {id: tag.id}})
      toast.success('Tag deleted successfully')
      refetch()
      onTagsUpdated?.()
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete tag')
    }
  }

  const handleCancel = () => {
    setEditingTag(null)
    setFormData({name: '', color: '#3699ff'})
  }

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>Manage Tags</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='tag-form mb-4'>
          <Form onSubmit={handleSubmit}>
            <div className='d-flex align-items-end gap-2 flex-wrap'>
              <Form.Group style={{flex: '2 1 220px'}}>
                <Form.Label className='mb-1 small'>Tag Name *</Form.Label>
                <Form.Control
                  type='text'
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder='e.g., Hot Lead, Follow Up'
                />
              </Form.Group>
              <Form.Group style={{flex: '0 0 auto'}}>
                <Form.Label className='mb-1 small'>Color</Form.Label>
                <Form.Control
                  type='color'
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  style={{width: '44px', height: '38px', padding: '2px'}}
                  title={formData.color}
                />
              </Form.Group>
              <div className='d-flex gap-2'>
                <Button type='submit' variant='primary' disabled={saving}>
                  {editingTag ? 'Update Tag' : 'Create Tag'}
                </Button>
                {editingTag && (
                  <Button type='button' variant='secondary' onClick={handleCancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </div>

        <div className='tags-list'>
          <h6 className='mb-3'>Existing Tags ({tags.length})</h6>
          {loading ? (
            <div className='text-center py-4'>
              <div className='spinner-border spinner-border-sm' />
            </div>
          ) : tags.length === 0 ? (
            <Alert variant='info'>No tags created yet. Create your first tag above.</Alert>
          ) : (
            <Table striped hover size='sm'>
              <thead>
                <tr>
                  <th style={{width: '40px'}}>Color</th>
                  <th>Name</th>
                  {isSuperAdmin && <th>Scope</th>}
                  <th style={{width: '120px'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tags.map((tag) => {
                  const manageable = canManage(tag)
                  return (
                    <tr key={tag.id}>
                      <td>
                        <span
                          className='badge rounded-pill px-2'
                          style={{backgroundColor: tag.color, color: getContrastColor(tag.color)}}
                        >
                          {tag.name}
                        </span>
                      </td>
                      <td>
                        <strong>{tag.name}</strong>
                      </td>
                      {isSuperAdmin && (
                        <td>
                          {tag.organization ? (
                            <Badge bg='secondary' text='light'>
                              {tag.organization.name}
                            </Badge>
                          ) : (
                            <Badge bg='dark' text='light' className='border border-secondary'>
                              Global
                            </Badge>
                          )}
                        </td>
                      )}
                      <td>
                        <div className='d-flex gap-1'>
                          <Button
                            size='sm'
                            variant='outline-primary'
                            disabled={!manageable}
                            title={manageable ? 'Edit tag' : "You don't have permission to edit this tag"}
                            onClick={() => handleEdit(tag)}
                          >
                            Edit
                          </Button>
                          <Button
                            size='sm'
                            variant='outline-danger'
                            disabled={!manageable}
                            title={manageable ? 'Delete tag' : "You don't have permission to delete this tag"}
                            onClick={() => handleDelete(tag)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
